import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroup } from '../contexts/GroupContext';
import {
  listSchedulerJobs,
  getSchedulerJob,
  activateSchedulerJob,
  pauseSchedulerJob,
  resumeSchedulerJob,
  deactivateSchedulerJob,
  clearSchedulerAttention,
  triggerSchedulerJob,
} from '../utils/api';
import { startActivityAwarePolling } from '../utils/activityAwarePolling';
import '../styles/SchedulerDashboard.css';

function formatDuration(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '—';
  const n = Number(ms);
  if (n < 1000) return `${n}ms`;
  const s = Math.round(n / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
}

const STATUS_LABELS = {
  inactive: 'Inactive',
  active: 'Active',
  paused: 'Paused',
  running: 'Running',
  error: 'Error',
  needs_attention: 'Needs attention',
};

/**
 * Settings → Scheduler — monitor toolchain schedulers (status, logs, controls).
 */
const SchedulerDashboard = () => {
  const { activeSuffix } = useGroup();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterSuffix, setFilterSuffix] = useState(activeSuffix || '');
  const [selected, setSelected] = useState(null); // detail job
  const [history, setHistory] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionKey, setActionKey] = useState(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listSchedulerJobs({
        suffix: filterSuffix || undefined,
      });
      setJobs(result.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load schedulers');
    } finally {
      setLoading(false);
    }
  }, [filterSuffix]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    return startActivityAwarePolling(loadJobs, 30_000, { immediate: false });
  }, [loadJobs]);

  useEffect(() => {
    if (activeSuffix && !filterSuffix) setFilterSuffix(activeSuffix);
  }, [activeSuffix, filterSuffix]);

  const openDetail = async (job) => {
    setSelected(job);
    setDetailLoading(true);
    try {
      const res = await getSchedulerJob(job.suffix, job.tool_slug, { limit: 20 });
      setSelected(res.data || job);
      setHistory(res.history || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const runAction = async (job, action) => {
    const key = `${job.suffix}:${job.tool_slug}:${action}`;
    setActionKey(key);
    setError('');
    try {
      const fns = {
        activate: activateSchedulerJob,
        pause: pauseSchedulerJob,
        resume: resumeSchedulerJob,
        deactivate: deactivateSchedulerJob,
        clearAttention: clearSchedulerAttention,
        trigger: triggerSchedulerJob,
      };
      const fn = fns[action];
      if (!fn) return;
      await fn(job.suffix, job.tool_slug);
      await loadJobs();
      if (selected && selected.tool_slug === job.tool_slug && selected.suffix === job.suffix) {
        await openDetail(job);
      }
      try {
        window.dispatchEvent(new Event('schedulerUpdate'));
      } catch (_) {
        /* ignore */
      }
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setActionKey(null);
    }
  };

  return (
    <div className="scheduler-dashboard">
      <div className="scheduler-dashboard-header">
        <div>
          <h3>
            <i className="fas fa-clock" /> Scheduler
          </h3>
          <p className="description">
            Run a prompt (LLM + tools) or a tool chain on a UTC cron schedule (15-minute minimum).
            Create schedulers under Settings → Tools, then activate them here.
            Chat can also activate, pause, resume, or run them now.
          </p>
        </div>
        <div className="scheduler-header-actions">
          <label className="scheduler-suffix-filter">
            Horizon
            <input
              className="form-control"
              value={filterSuffix}
              onChange={(e) => setFilterSuffix(e.target.value.trim())}
              placeholder="suffix filter (optional)"
            />
          </label>
          <button type="button" className="btn btn-secondary btn-sm" onClick={loadJobs} disabled={loading}>
            <i className="fas fa-sync" /> Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate('/settings/tools')}
          >
            <i className="fas fa-plus" /> Create in Tools
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading && jobs.length === 0 ? (
        <p className="scheduler-muted">Loading schedulers…</p>
      ) : jobs.length === 0 ? (
        <div className="scheduler-empty">
          <p>No scheduler jobs yet.</p>
          <p className="scheduler-muted">
            Create a tool with type <strong>Scheduler</strong>, then return here to set it Active.
          </p>
        </div>
      ) : (
        <div className="scheduler-table-wrap">
          <table className="scheduler-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Horizon</th>
                <th>Schedule</th>
                <th>Status</th>
                <th>Last run</th>
                <th>Duration</th>
                <th>Errors</th>
                <th>Next</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const busy = actionKey && actionKey.startsWith(`${job.suffix}:${job.tool_slug}:`);
                return (
                  <tr
                    key={`${job.suffix}:${job.tool_slug}`}
                    className={selected?.tool_slug === job.tool_slug && selected?.suffix === job.suffix ? 'is-selected' : ''}
                  >
                    <td>
                      <button type="button" className="scheduler-link" onClick={() => openDetail(job)}>
                        {job.tool_name || job.tool_slug}
                      </button>
                      <div className="scheduler-slug">{job.tool_slug}</div>
                    </td>
                    <td>
                      <code>{job.suffix}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.85em' }}>
                        {job.cron_expr || job.tool_metadata?.cron_expr || `${job.period_hours || 24}h`}
                      </code>
                    </td>
                    <td>
                      <span className={`scheduler-status scheduler-status--${job.status}`}>
                        {STATUS_LABELS[job.status] || job.status}
                      </span>
                    </td>
                    <td>{formatTs(job.last_finished_at || job.last_started_at)}</td>
                    <td>{formatDuration(job.last_duration_ms)}</td>
                    <td>{job.consecutive_errors || 0}</td>
                    <td>{formatTs(job.next_run_at)}</td>
                    <td className="scheduler-actions">
                      {job.status === 'inactive' && (
                        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => runAction(job, 'activate')}>
                          Activate
                        </button>
                      )}
                      {job.status === 'active' && (
                        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => runAction(job, 'pause')}>
                          Pause
                        </button>
                      )}
                      {job.status === 'paused' && (
                        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => runAction(job, 'resume')}>
                          Resume
                        </button>
                      )}
                      {(job.status === 'error' || job.status === 'needs_attention') && (
                        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => runAction(job, 'clearAttention')}>
                          Clear &amp; activate
                        </button>
                      )}
                      {job.status !== 'inactive' && job.status !== 'running' && (
                        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => runAction(job, 'deactivate')}>
                          Deactivate
                        </button>
                      )}
                      {job.status !== 'running' && (
                        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => runAction(job, 'trigger')}>
                          Run now
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => openDetail(job)}>
                        Logs
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="scheduler-detail">
          <div className="scheduler-detail-header">
            <h4>
              {selected.tool_name || selected.tool_slug}
              <span className={`scheduler-status scheduler-status--${selected.status}`}>
                {STATUS_LABELS[selected.status] || selected.status}
              </span>
            </h4>
            <button type="button" className="btn btn-sm" onClick={() => { setSelected(null); setHistory([]); }}>
              Close
            </button>
          </div>
          {selected.last_error && (
            <div className="alert alert-error">{selected.last_error}</div>
          )}
          <div className="scheduler-stdout-block">
            <h5>Last stdout</h5>
            {detailLoading ? (
              <p className="scheduler-muted">Loading…</p>
            ) : (
              <pre className="scheduler-stdout">{selected.last_stdout || '(no output yet)'}</pre>
            )}
          </div>
          <div className="scheduler-history-block">
            <h5>Recent runs</h5>
            {history.length === 0 ? (
              <p className="scheduler-muted">No history yet.</p>
            ) : (
              <ul className="scheduler-history-list">
                {history.map((h) => (
                  <li key={h.id}>
                    <span className={`scheduler-status scheduler-status--${h.status === 'active'}`}>
                      {h.status}
                    </span>
                    {' '}
                    {formatTs(h.started_at)} · {formatDuration(h.duration_ms)}
                    {h.error ? <div className="scheduler-history-error">{h.error}</div> : null}
                    {h.stdout ? (
                      <pre className="scheduler-stdout scheduler-stdout--compact">{h.stdout}</pre>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulerDashboard;
